import React, {
	useEffect, useRef, useState,
} from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import 'rapidoc';

import { fetchData, styleShadowDOM, styleDynamicShadowDOM } from '../../../helpers/methods';

import SpinnerContainer from '../../../component-library/spinner-container';

import TryItKey from './sub-components/try-it-key';
import rapidocStyles from './sub-components/rapidoc-styles';
import rapidocApiRequestStyles from './sub-components/rapidoc-api-request-styles';

const InteractiveDocumentation = () => {
	const { id } = useParams();
	const { storefrontData } = useOutletContext();
	const rapidocRef = useRef();
	const api = storefrontData?.api;
	const [loading, setLoading] = useState(true);
	const [allowApi, setAllowApi] = useState(false);
	const allowApiRef = useRef();
	allowApiRef.current = allowApi;
	const [allowMock, setAllowMock] = useState(false);
	const allowMockRef = useRef();
	allowMockRef.current = allowMock;
	const [tryItLoading, setTryItLoading] = useState(false);
	const [error, setError] = useState(false);
	const [runtimeError, setRuntimeError] = useState(false);
	const [openApiData, setOpenApiData] = useState({});
	const [apiKey, setApiKey] = useState(null);
	const [expired, setExpired] = useState(false);
	// Current state of try it out - mock/key/none
	const [currentTryMode, setCurrentTryMode] = useState('none');

	const getOpenApiData = mocked => {
		setLoading(true);
		fetchData({
			uri: `/api/web/catalog/item/${id}/openapi/docs?useMocking=${mocked}`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					body.text().then(content => {
						setOpenApiData(JSON.parse(content));
						setLoading(false);
					});
				} else {
					setLoading(false);
					setError(true);
				}
			})
			.catch(() => {
				setLoading(false);
				setError(true);
			});
	};

	const checkForValidKey = runtimeData => {
		let newApiKey = null;
		let newExpired = false;
		const developerData = runtimeData.platformRuntime.developer;
		const developerApps = developerData.apps;
		const tryItApp = developerApps.find(item => item.name === 'digitalmlautocreated');
		if (tryItApp != null) {
			const tryItAppCredentials = tryItApp.credentials;

			if (tryItAppCredentials?.length > 0) {
				tryItAppCredentials.forEach(cred => {
					if (cred.products.find(e => e.apiProduct === api)) {
						newApiKey = cred.consumerKey;
						const newExpiresAt = cred.expiresAt;

						if (newExpiresAt) {
							const inputDate = new Date(newExpiresAt);
							const currentDate = new Date();

							if (inputDate < currentDate) {
								newExpired = true;
							}
						}
					}
				});
			}

			if (newApiKey && !newExpired) {
				setCurrentTryMode('key');
				getOpenApiData(false);
			} else if (allowMockRef.current) {
				setCurrentTryMode('mock');
				getOpenApiData(true);
			} else {
				setCurrentTryMode('key');
				getOpenApiData(false);
			}
		} else if (allowMockRef.current) {
			setCurrentTryMode('mock');
			getOpenApiData(true);
		} else {
			setCurrentTryMode('key');
			getOpenApiData(false);
		}
		setApiKey(newApiKey);
		setExpired(newExpired);
		setTryItLoading(false);
	};

	const getNewApiKey = () => {
		setTryItLoading(true);
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime`,
			method: 'POST',
		})
			.then(({ ok, body }) => {
				if (ok) {
					checkForValidKey(body);
				} else {
					setTryItLoading(false);
				}
			})
			.catch(() => {
				setTryItLoading(false);
				if (allowMockRef.current) {
					setCurrentTryMode('mock');
					getOpenApiData(true);
				} else {
					setCurrentTryMode('key');
					getOpenApiData(false);
				}
			});
	};

	const getDeveloperData = () => {
		setTryItLoading(true);
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					checkForValidKey(body);
				} else {
					const { message } = body;
					if (message?.includes('does not exist') || message?.includes('No such file or directory')) {
						setRuntimeError(true);
					}
					setTryItLoading(false);
					setLoading(false);
				}
			})
			.catch(() => {
				setTryItLoading(false);
				if (allowMockRef.current) {
					setCurrentTryMode('mock');
					getOpenApiData(true);
				} else {
					setCurrentTryMode('key');
					getOpenApiData(false);
				}
			});
	};

	const getConfig = () => {
		fetchData({
			uri: `/api/web/catalog/item/${id}/interactivedocs/config`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const conf = body;
					if (conf.allowedAPIKey) {
						setAllowApi(true);
						getDeveloperData(conf.allowedMock);
					}
					if (conf.allowedMock) {
						setAllowMock(true);
					}
					if (!conf.allowedAPIKey && conf.allowedMock) {
						setCurrentTryMode('mock');
						getOpenApiData(true);
					}
					if (!conf.allowedAPIKey && !conf.allowedMock) {
						setCurrentTryMode('none');
						getOpenApiData(false);
					}
				} else {
					setCurrentTryMode('none');
					getOpenApiData(false);
				}
			})
			.catch(() => {
				setCurrentTryMode('none');
				getOpenApiData(false);
			});
	};

	const setAuthKey = () => {
		// Figure out the name of the apiKey security schema.
		const {
			components: {
				securitySchemes = {},
			} = {},
		} = openApiData;
		Object.entries(securitySchemes).forEach(([name, content]) => {
			if (content.type === 'apiKey') {
				rapidocRef.current.setApiKey(name, apiKey);
			}
		});
	};

	const stylePanelContents = () => {
		const rapidoc = rapidocRef.current.shadowRoot;
		const mainContent = rapidoc.querySelector('.main-content-inner--focused-mode');
		styleDynamicShadowDOM('api-request', mainContent, rapidocApiRequestStyles);
	};

	useEffect(() => {
		if (rapidocRef.current) {
			rapidocRef.current.loadSpec(openApiData);
			rapidocRef.current.addEventListener('spec-loaded', () => {
				styleShadowDOM(rapidocRef.current, rapidocStyles);
				if (apiKey) {
					setAuthKey();
				}
			});
			styleShadowDOM(rapidocRef.current, rapidocStyles);
			if (currentTryMode === 'mock') {
				// For Mock - Set the Authorization header and check for JSON requests
				rapidocRef.current.addEventListener('before-try', e => {
					if (currentTryMode === 'mock') {
						e.detail.request.headers.append(
							'Authorization', `Bearer ${sessionStorage.getItem('store_kc_token')}`,
						);
					}
				});
			}
		}
	}, [openApiData, loading]);

	useEffect(() => {
		if (rapidocRef.current) {
			if (apiKey) {
				setAuthKey();
			}
		}
	}, [apiKey]);

	useEffect(() => {
		getConfig();
	}, []);

	return (
		<>
			{loading && (
				<SpinnerContainer />
			)}
			{!runtimeError ? (
				<>
					{!loading && !error && (
						<>
							{allowApi && (
								<TryItKey
									apiKey={apiKey}
									expired={expired}
									getNewApiKey={() => getNewApiKey()}
									loading={tryItLoading}
									allowMock={allowMock}
								/>
							)}
							<rapi-doc
								allow-authentication={String(allowMock && currentTryMode === 'mock')}
								allow-search="false"
								allow-advanced-search="false"
								allow-server-selection="false"
								allow-spec-file-load="false"
								allow-spec-url-load="false"
								allow-try={String(currentTryMode === 'mock' || (currentTryMode === 'key' && apiKey !== null))}
								onClick={() => { stylePanelContents(); }}
								bg-color="#FCFCFC"
								header-color="#FCFCFC"
								nav-bg-color="#F6F6F6"
								nav-text-color="#1D1D1D"
								primary-color="#1D1D1D"
								ref={rapidocRef}
								regular-font="'Open Sans', sans-serif"
								render-style="focused"
								layout="column"
								style={{ padding: '6px 0 12px' }}
								theme="light"
								update-route="false"
								show-method-in-nav-bar="as-colored-text"
								show-header="false"
								use-path-in-nav-bar="true"
							/>
						</>
					)}
					{error && (
						<>
							<h3>Oops! Something went wrong</h3>
							<p className="large-text margin-bottom-32">Please refresh the page and try again, if the problem persists please contact your system administrator.</p>
						</>
					)}
				</>
			) : (
				<>
					<h3>Oops! Something went wrong</h3>
					<p className="large-text margin-bottom-32">There was an issue with the spec, unable to load documentation, please contact provider.</p>
				</>
			)}
		</>
	);
};

export default InteractiveDocumentation;
